import React, { useEffect,  ChangeEvent, useMemo } from "react";
import { animated, useSpringRef, useTransition } from "@react-spring/web";
import { MediaLibraryChooser } from "src/components/mediaLibrary";
import { BannerFilters } from "./components/filter";
import { CropEditor } from "./components/crop";
import cx from 'classnames';
import styles from "./styles.module.scss";
import { MediaLibrary } from "src/types/mediaLibrary";
import { nanoid } from "nanoid";
import { MediaFile } from "src/types/media";
import { get } from "lodash";
import Actions from "src/components/actionbar";
import { useUploadMedia } from "src/query/settings/uploadMedia";
import { parseMediaMetadata } from "src/utils/media.service";
import { toast } from "react-toastify";
import { useViewerSettingsBannerAdoption } from "src/query/settings/adoption/viewerSettings";
import { POSITIONS } from "src/constants/banner";

export interface MenuState {
  primaryAction: 'view' | 'edit' | 'edit-banner';
  secondaryAction?: 'save' | 'reset' | 'add-section' | 'crop' | 
  'apply-crop' | 'filter' | 'rename' | 'add-asset' | 'position' | 'asset-viewer' | 'editing' |
  'social-share' | 'reset-layout' | null;
  data?: any;
}
interface BannerEditorProps {
  show: boolean;
  classnames?: string;
  banner?: React.ReactNode;
  librarySettings?: any;
  onChange?: (value: any) => void;
  onClose?: () => void;
}

export const BannerEditor: React.FC<BannerEditorProps> = ({
  show,
  classnames,
  banner,
  librarySettings,
  onChange,
  onClose,
}) => {

   const { trackBannerFilteredEvent, 
         trackBannerCropped, 
         trackAddBannerClicked, 
         trackPageViewed, trackBannerContentSelected } = useViewerSettingsBannerAdoption();

   const [menuState, setMenuState] = React.useState<MenuState>({
     primaryAction: 'edit-banner'
   });
 
   const primaryAction = get(menuState, "primaryAction");
   const secondaryAction = get(menuState, "secondaryAction");

   const showMediaLibrary  = primaryAction === "edit-banner" && !secondaryAction;
   const showCrop = primaryAction === "edit-banner" &&  (secondaryAction === "crop" || secondaryAction === "apply-crop");
   const showFilters = primaryAction === "edit-banner" && secondaryAction === "filter";
   const showPosition = primaryAction === "edit-banner" && secondaryAction === "position";

   const bannerContentRef = useSpringRef();
   const bannerContentTransitions = useTransition(show, {
        ref: bannerContentRef,
        from: {  opacity: 0,},
        enter: { opacity: 1,  delay: 500,},
        leave: { opacity: 0,},
        config: {
        friction: 14,
        tension: 120,
        },
   });

    useEffect(() => {
        bannerContentRef.start();
    }, [show]);

    useEffect(() => { // Media Library Opened
      if(showMediaLibrary && show){
        trackAddBannerClicked({ sourceCta: "Collection" });
        trackPageViewed();
      }
    }, [showMediaLibrary, show]);

    /* Methods */
    const handleSaveChanges = () => { // save changes (crop & position)
        if (showCrop) {
            setMenuState({
                ...menuState,
                secondaryAction: 'apply-crop'
            });
        } else if (showFilters) {
            setMenuState({
                ...menuState,
                secondaryAction: null
            });      
        }
    }

    // update banner
    const handleFileChange = (background: MediaFile) => {
      let banner = librarySettings.banner;
      if(!banner) {
        banner = {
          position: 'top',
          autoHeightEnabled: true,
          sharedByEnabled: true,
        };
      }
      
       trackBannerContentSelected({
        bannerType: background?.type || "",
        sourceCta: "Collection"
       });
       
       onChange?.({
            ...librarySettings.banner,
            background: background,
            crop: null,
        });
    }

    const handleBannerSelection = (media: MediaLibrary) => {
      if (!media) {
        return;
      }
  
      const imageUrl = media.imageUrl;
      if(!imageUrl) {
        return;
      }
  
      fetch(imageUrl)
      .then((response) => {
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
  
        const backgroundMedia = {
          id: nanoid(6),
          entityType: 'media-preview',
          type: media.mediaType,
          full: url,
          thumbnail: url,
          width: media.width,
          height: media.height,
          contentType: blob.type,
        } as MediaFile
      
        handleFileChange(backgroundMedia);
      }).catch(error => {
        console.error(error);
      });
    }

  return (
    <> <div className={cx(styles.bannerContainer, classnames)}>
       <div className={styles.bannerWrapper}>

            <div className={cx(styles.banner, {
                    [styles.show]: show,
                    [styles.hide]: !show,
                })}>
                { !showCrop && banner}
            </div>
          
           <div className={cx(styles.menuBarWrapperTop, {
                [styles.show]: show, })}>
                        { !showFilters && !showCrop && !showPosition && 
                           <EditBannerMenu
                            entity={librarySettings}
                            menuState={menuState}
                            onMenuChange={ (val) => {
                                if(val.primaryAction === "view"){
                                    onClose?.();
                                } else {
                                 setMenuState(val);
                                }
                            }}
                            onChanges={(field, val) => onChange?.(val.banner)}
                            entityField="librarySettings"
                            />}

                        { (showFilters || showCrop) && 
                          <ConfirmationMenu 
                           onConfirm={handleSaveChanges} 
                           onCancel={() => setMenuState({...menuState, secondaryAction: null})}
                           />
                        }
                        
                        { showPosition && <BannerPositionMenu
                            entitySettings={librarySettings}
                            menuState={menuState}
                            onMenuChange={ setMenuState }
                            onChange={(field, val) =>  onChange?.(val.banner)}
                            entityField="librarySettings"
                        />
                        }
            </div>
      </div>
    </div>

    {bannerContentTransitions((style, item) => item && (
        <animated.div style={style} className={cx(styles.container, {
                [styles.cropContainer]: showCrop,
                [styles.scrollY]: showMediaLibrary || showFilters
            })}>

                 { showMediaLibrary && 
                   <div className={styles.innerContainer}>
                   <MediaLibraryChooser 
                    entityType="collection"
                    onChange={handleBannerSelection} 
                    columns={5}/> 
                    </div>
                }

                { showFilters && 
                 <div className={styles.innerContainer}>
                     <BannerFilters
                        collection={librarySettings}
                        onChange={(field, val)=> onChange?.(val)}
                        onFilterClick={(field, val) => {
                          trackBannerFilteredEvent({ bannerType: val?.background?.type || "", cta: "" });
                        }}
                        />
                 </div>
                }

                { showCrop &&
                    <CropEditor
                       enabled
                       menuState={menuState}
                       onMenuChange={setMenuState} 
                       entity={librarySettings}
                        onChange={(field, val) =>{
                          trackBannerCropped({ collectionName: "", sourceCta: "Collection" });
                          onChange?.(val);
                        }}
                    />
                }
            </animated.div>
     ))} </>
  );
};

interface EditBannerMenuProps {
  entity: any;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
  onChanges: (field: string, value: any) => void;
  entityField: string;
}

// Edit Banner Menu
const EditBannerMenu: React.FC<EditBannerMenuProps> = ({ entity, menuState, onMenuChange, onChanges,entityField }) => {

  const { trackFilterBannerClicked, trackCropBannerClicked } = useViewerSettingsBannerAdoption();

  const originalEntity = React.useRef(entity);
  const [entityState, setEntityState] = React.useState({ ...entity })

  const { mutate: uploadMediaMutate, isPending: uploading } = useUploadMedia();

  const canCrop = useMemo(() => {
    return entityState?.banner?.background?.type === 'image';
  }, [entityState]);


  const handleBannerFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Please upload a file of size less than 10MB");
      return;
    }

    const backgroundMedia = await parseMediaMetadata(file);

    const updatedEntity = {
      ...entity,
      banner: {
        ...(entityState.banner || {}),
        background: backgroundMedia,
        crop: null,
      },
    };

    onChanges(entityField, updatedEntity);
  };

  const handleSaveBanner = async () => {
    const banner = get(entity, 'banner');
    const media = get(banner, 'background');
    const entityType = get(media, 'entityType');

    const bannerType = get(media, 'type', '');

    if (media && entityType === 'media-preview') {
      const mediaFile = media as MediaFile;
      const blob = await fetch(media!.full!).then(r => r.blob());
      const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

      uploadMediaMutate({
        ...media,
        file: file
      } as MediaFile, {
        onSuccess: (response) => {
          const updatedEntity = {
            ...entity,
            banner: {
              ...entity.banner,
              background: response,
            },
          };

          setEntityState(updatedEntity);

          onChanges(entityField, updatedEntity);

          onMenuChange({ ...menuState, primaryAction: 'view' });
        },
        onError: (err) => {
          console.error("Media upload failed", err);
        }
      });

    } else {
      onMenuChange({
        ...menuState,
        primaryAction: 'view'
      });
    }
  };


  const handleCancel = () => {
    setEntityState(originalEntity.current);

    onChanges(entityField, originalEntity.current);

    onMenuChange({ ...menuState, primaryAction: 'view' });
  };

  const handleCrop = () => { // Crop

    trackCropBannerClicked({
      sourceCta: "Collection"
    });

    onMenuChange({
      ...menuState,
      secondaryAction: 'crop'
    });
  }

  const handleFilter = () => { // Filter

    trackFilterBannerClicked({
       bannerType: entityState?.banner?.background?.type || "",
       cta: ""
    });

    onMenuChange({
      ...menuState,
      secondaryAction: 'filter'
    });
  }

  const handlePosition = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: 'position'
    });
  }

  React.useEffect(() => {
    setEntityState({ ...entity });
  }, [entity]);

  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.FileUpload width={5}
          icon="paperflite-upload"
          title="Upload from device"
          accept="image/*,video/*"
          disabled={uploading}
          onClick={() => (entity)}
          onChange={handleBannerFileChange}
        />
        {canCrop &&
          <>
            <Actions.IconButton
              icon="paperflite-crop"
              tooltip="Crop"
              disabled={uploading}
              onClick={handleCrop}
            />
            <Actions.IconButton
              icon="paperflite-focus"
              tooltip="Position"
              disabled={uploading}
              onClick={handlePosition}
            />
          </>
        }

        {!canCrop &&

          <Actions.Button
            icon="paperflite-focus"
            width={2}
            title="Position"
            disabled={uploading}
            onClick={handlePosition}
          />
        }

      </Actions.Row>
      <Actions.Row>
        <Actions.Button width={2}
          icon="paperflite-filter-property"
          title="Filters"
          disabled={uploading}
          onClick={handleFilter}
        />
        <Actions.Button width={3}
          icon="paperflite-tick"
          title={uploading ? 'Saving...' : 'Save Changes'}
          className={styles.primaryAction}
          disabled={uploading}
          onClick={handleSaveBanner}
        />
        <Actions.Button width={2}
          icon="paperflite-close"
          title="Cancel"
          disabled={uploading}
          onClick={handleCancel}
        />
      </Actions.Row>
    </Actions.Container>
  );
}

interface ConfirmationMenuProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationMenu: React.FC<ConfirmationMenuProps> = ({ onConfirm, onCancel }) => {
  return (
    <Actions.Container>
      <Actions.Row>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Done" onClick={onConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={onCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}

interface BannerPositionMenuProps {
  entitySettings: any;
  entityField: string;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
  onChange: (field: string, value: any) => void;
}

export const BannerPositionMenu: React.FC<BannerPositionMenuProps> = ({ entitySettings, menuState, onMenuChange, onChange, entityField}) => {
  const Banner = entitySettings?.banner || {};
  const positionValue = Banner.position || '';
  
  const handlePositionChange = (position: string) => {
    const currentBanner = entitySettings?.banner || {};
    const updatedBanner = {
      ...currentBanner,
      position,
    };
  
    onChange(entityField, {
      ...entitySettings,
      banner: {
        ...currentBanner,
        ...updatedBanner,
      },
    });
  };

  const handleConfirm = () => {
    onMenuChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  const handleCancel = () => {
    onChange(entityField, {
      ...entitySettings,
      banner: Banner,
    });

    onMenuChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  return (
    <Actions.Container>
      <Actions.Row className={styles.positionActionRow}>
        <Actions.Item className={styles.positions}>
          {POSITIONS.map((position) => {
            const active = position.value === positionValue;
            return (
              <div key={position.value}
                className={cx(styles.position, {
                  [styles.active]: active
                })}
                onClick={() => handlePositionChange(position.value)}
              />
            );
          })}
        </Actions.Item>
        <Actions.Button width={3} icon="paperflite-tick" className={styles.primaryAction} title="Save" onClick={handleConfirm} />
        <Actions.Button width={3} icon="paperflite-close" title="Cancel" onClick={handleCancel} />
      </Actions.Row>
    </Actions.Container>
  );
}